import React, { useCallback, useRef } from "react";
import "./calendar.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useState } from "react";
import { csCZ } from "@mui/x-date-pickers";
import localizedFormat from "dayjs/plugin/localizedFormat";
import dayjs from "dayjs";
import { useEffect } from "react";
import "dayjs/locale/cs"; // Import Czech locale
import ContentCutIcon from "@mui/icons-material/ContentCut";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import EmployeeSelect from "../employeeSelect/employeeeSelect";
import {convertMinutesToTime, convertTimeToMinutes} from "../../utils/timeUtils";
import {getEmployeeClosedDays, getEmployeeOpeningHours} from "../../models/employees";

dayjs.extend(localizedFormat);
dayjs.locale("cs"); // Use Czech locale globally

//TODO: POŘEŠIT S CHATGPT jak zamezit dvojitým rezervacím - otestovat to.

export const Calendar = ({
  selectedEmployee,
  onSelectedEmployee,
  setSelectedDateRecord,
  setSelectedTimeRecord,
  serviceTimeTotal,
  servicesTotalPrice,
  setStep,
  bookedDates,
  selectedService,
}) => {
  const getTimeBlocks = useCallback(
    (date, serviceTimeTotal) => {
      if (serviceTimeTotal === 25)
          serviceTimeTotal = 30;

      const schedule = getEmployeeOpeningHours(selectedEmployee, date.day());
      console.log("schedule: ", schedule);
      if (schedule.isOpen === false) {
        return [];
      }
      const openingHours = convertTimeToMinutes(schedule.openingHours.open);
      const closingHours = convertTimeToMinutes(schedule.openingHours.close);
      const lunchStart = convertTimeToMinutes(selectedEmployee.lunchBreak.start);
      const lunchEnd = convertTimeToMinutes(selectedEmployee.lunchBreak.end);
      const dinnerStart = convertTimeToMinutes(selectedEmployee.dinnerBreak.start);
      const dinnerEnd = convertTimeToMinutes(selectedEmployee.dinnerBreak.end);

      const formattedSelectedDate = date.format("DD.MM.YYYY");

      const blocks = [];

      for (let time = openingHours;
        time <= closingHours - serviceTimeTotal;
        time += serviceTimeTotal)
      {
        const endTime = time + serviceTimeTotal;
        if (endTime > closingHours)
            break;

        const now = `${dayjs().hour()}:${dayjs().minute()}`;
        if (date.isSame(dayjs(), "day") && time < convertTimeToMinutes(now)) {
            continue;
        }

        // Skip lunch break
        if ((time >= lunchStart && time < lunchEnd) ||
          (endTime > lunchStart && endTime <= lunchEnd)) {
          continue;
        }

        // Skip dinner break
        if ((time >= dinnerStart && time < dinnerEnd) ||
          (endTime > dinnerStart && endTime <= dinnerEnd)) {
          continue;
        }

        let formattedStartTime = convertMinutesToTime(time);
        convertMinutesToTime(endTime);

        const isBooked = bookedDates.some((booking) => {
          if (booking.date !== formattedSelectedDate) return false;

          const bookingStart = convertTimeToMinutes(booking.startTime);
          const bookingEnd = convertTimeToMinutes(booking.finishTime);

          return time < bookingEnd && endTime > bookingStart;
        });

        if (!isBooked) {
          blocks.push(`${formattedStartTime}`);
        }
      }
      return blocks;
    },
    [bookedDates, selectedEmployee],
  );

  // Function to find the next available date based on service time total
  const findNextAvailableDate = useCallback((startDate, serviceTime) => {
    let checkDate = dayjs(startDate);
    let closedDates = getEmployeeClosedDays(selectedEmployee.id);
    while (getTimeBlocks(checkDate, serviceTime).length === 0 || closedDates.includes(checkDate.day()))
    {
      checkDate = checkDate.add(1, "day");
    }
    return checkDate;
  }, [getTimeBlocks, selectedEmployee]);

  const [date, setDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(findNextAvailableDate(dayjs(date), serviceTimeTotal));
  const [availableTimeBlocks, setAvailableTimeBlocks] = useState([]);

  useEffect(() => {
    if (selectedDate && selectedDate.format) {
      const newAvailableTimeBlocks = getTimeBlocks(
        selectedDate,
        serviceTimeTotal,
      );
      setAvailableTimeBlocks(newAvailableTimeBlocks);
    }
  }, []);

  findNextAvailableDate(dayjs(date), serviceTimeTotal);
  const disabledDates = [];

  useEffect(() => {
    setSelectedDateRecord(selectedDate);
  }, [selectedDate]);
  useEffect(() => {
    // Check if the date is disabled, directly passing the date to be checked
    if (shouldDisableDate(date)) {
      // If the date is disabled, move to the next day
      setDate(date.add(1, "day"));
    }
  }, [date]);
  useEffect(() => {
    // Ensure selectedDate is always a dayjs object
    if (selectedDate && selectedDate.format) {
      const newAvailableTimeBlocks = getTimeBlocks(
        selectedDate,
        serviceTimeTotal,
      );
      setAvailableTimeBlocks(newAvailableTimeBlocks);
    }
  }, [getTimeBlocks, selectedDate, serviceTimeTotal]);
  const headerRef = useRef(null);

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleDateChange = (newDate) => {
    setSelectedDate(dayjs(newDate)); // This should ensure 'selectedDate' is always a dayjs object
    setSelectedDateRecord(dayjs(newDate));
  };
  const handleTimeBlockClick = (timeBlock) => {
    setSelectedTimeRecord(timeBlock);
    setStep(3);
  };
  const shouldDisableDate = useCallback((date) => {
    const dayjsDate = dayjs(date);
    const closedDays = getEmployeeClosedDays(selectedEmployee.id);
    const isClosed = closedDays.includes(dayjsDate.day());
    const isDisabledDate = disabledDates.some((disabledDate) =>
      dayjsDate.isSame(dayjs(disabledDate, "DD.MM.YYYY"), "day"),
    );
    const noAvailableBlocks = getTimeBlocks(dayjsDate, serviceTimeTotal).length === 0;

    return isClosed || isDisabledDate || noAvailableBlocks;
    }, [disabledDates, serviceTimeTotal]);

  return (
    <div className="reservationBlock" ref={headerRef}>
      <h2 className="reservationTitle"> Zvolte datum a čas služby</h2>
      <h4 className="employeeSelectLabel">Vyberte zaměstnance:</h4>
      <EmployeeSelect
        className="employeeSelect"
        selectedEmployee={selectedEmployee}
        onSelectedEmployee={onSelectedEmployee}
      />

      <div className="calendarWrapper">
        <div className="datePicker">
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="cs"
            localeText={
              csCZ.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <DateCalendar
              label="Date"
              inputFormat="DD.MM.YYYY"
              fixedWeekNumber={6}
              shouldDisableDate={shouldDisableDate}
              views={["day", "month"]}
              date={findNextAvailableDate(dayjs(), serviceTimeTotal)}
              minDate={findNextAvailableDate(dayjs(), serviceTimeTotal)}
              maxDate={dayjs().add(60, "day")}
              disableHighlightToday
              value={selectedDate}
              onChange={handleDateChange}
              showDaysOutsideCurrentMonth
            />
          </LocalizationProvider>
        </div>
        <div className="timeBlocksWrapper">
          <div className="timeBlocks">
            {availableTimeBlocks.length > 0 &&
              availableTimeBlocks.map((block, index) => (
                <div
                  key={index}
                  className="timeBlock"
                  onClick={() => handleTimeBlockClick(block)}
                >
                  {block}
                </div>
              ))}
          </div>
        </div>
      </div>
      {selectedService.nazev && (
        <div className="calendar-overview">
          <ContentCutIcon className="overViewIconCal" />{" "}
          <span>
            Vybraná služba:<b> {selectedService.nazev}</b>
            <CalendarMonthIcon className="overViewIconCal" />
            Datum: <b>{selectedDate.format("DD.MM.YYYY")}</b>
            <HourglassEmpty className="overViewIconCal" />
            Délka návštěvy <b>{serviceTimeTotal}min</b>
            <AttachMoneyIcon className="overViewIconCal" />
            Cena: <b>{selectedService.cena + servicesTotalPrice} Kč</b>
          </span>
        </div>
      )}
    </div>
  );
};
